import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { apps } from '../../../constants';
import { DateMomentPipe } from '../../../pipes';
import { DebugLoggerService } from '../../../services/debug-logger.service';
import { AppStateHolder, TimeLine } from '../../../shared';
import { UserActivitiesService } from './user-activities.service';

@Component({
    selector: 'calendar-user-activities',
    templateUrl: 'user-activities.component.html',
    styleUrls: ['./user-activities.component.scss'],
    standalone: true,
    imports: [TooltipModule, SkeletonModule, DatePipe, TranslateModule, TagModule, DateMomentPipe]
})

export class UserActivitiesComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  appStateHolder = inject(AppStateHolder);
  private timeLine = inject(TimeLine);
  private service = inject(UserActivitiesService);
  private translateService = inject(TranslateService);
  private _debugLogger = inject(DebugLoggerService);


  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;
  @Input() params: string;
  @Input() changedParams: any;
  @Input() widgetPosition: any;

  @Output() dimensionSelected: EventEmitter<any> = new EventEmitter();
  @Output() popup: EventEmitter<{ widx: number, pop: boolean, params: any }> = new EventEmitter();

  requestsSubscribe: Subscription;

  // Mappatura degli stati per i tag
  stateConfig = {
    0: { severity: 'danger', icon: 'pi pi-folder-open' },
    1: { severity: 'warning', icon: 'pi pi-bolt' },
    2: { severity: 'success', icon: 'pi pi-check' }
  };

  status: string;
  activities: any[];
  openOptions: boolean = false;
  optionSelected: any;

  finalDate: string;
  initialDate: string;
  typologies: any;
  state: any;
  
  shortDate: any = [{ format: "L" }];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {

  }

  ngOnInit(): void {
    this._debugLogger.logLifecycle('UserActivities', 'ngOnInit');
    if (!this.modify) {
      this.timeLine.init();
      this.loadActivities(this.params);
    }
  }


  loadActivities(params: string) {
    this._debugLogger.logAction('UserActivities', 'loadActivities', { initialDate: this.initialDate, finalDate: this.finalDate, state: this.state });
    this.activities = undefined;

    if (params) {
      let prms = JSON.parse(params);
      prms = prms.params ? JSON.parse(prms.params) : prms;

      if (prms.date == 'Day')
        this.dataClick(0);
      if (prms.date == 'Week')
        this.dataClick(1);
      if (prms.date == 'Month')
        this.dataClick(2);

      this.state = prms.state;
    }
    else {
      this.dataClick(1);
      this.state = "All";
      this.typologies = null;
    }

    this.requestsSubscribe = this.service.getUserActivitiesFil(this.initialDate, this.finalDate, this.state, this.typologies)
      .subscribe(result => {
        this._debugLogger.logAction('UserActivities', 'activities loaded', { count: result?.length });
        this.activities = result;
      });
  }

  onSelectActivity(activity: any): void {
    this._debugLogger.logAction('UserActivities', 'selectActivity', { id: activity.id });
    let url = this.appStateHolder.getAppUrl(apps.calendar);
    this.appStateHolder.setAppNavbarClass(apps.calendar);

    if (url.length === 0) {
      let link = ['/activity', activity.id];
      this._debugLogger.logNavigation(link.join('/'));
      this.router.navigate(link)
      return;
    }

    let link = ['/app', `${url}/activity/${activity.id}`];
    this._debugLogger.logNavigation(link.join('/'));
    this.router.navigate(link);
  }

  options() {
    this.openOptions = !this.openOptions;
  }

  selected() {
    this.dimensionSelected.next(this.optionSelected);
  }

  actpop() {
    this._debugLogger.logEvent('UserActivities', 'popup emitted', { idx: this.idx });

    if (this.changedParams == undefined) {
      this.sendParams();
    }
    else {
      let val = this.appStateHolder.activityParams.filter(r => { return r.indx == this.idx });

      if (val.length > 0) {
        this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(val[0].params) });
      }
      else {
        this.sendParams();
      }
    }
  }

  sendParams() {
    if (this.params) {
      this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(this.params) });
    }
    else {
      this.popup.next({ widx: this.idx, pop: true, params: this.params });
    }
  }

  dataClick(dateType: number) {

    let day;
    let week;
    let month;

    switch (dateType) {
      case 0:

        day = this.timeLine.day.date;

        this.finalDate = this.initialDate = (day.getFullYear() + "/" + (day.getMonth() + 1) + "/" + day.getDate());
        break;
      case 1:

        week = this.timeLine.week;
        let d1 = week.days[0].date;
        let d2 = week.days[week.days.length - 1].date;

        this.initialDate = d1.getFullYear() + "/" + (d1.getMonth() + 1) + "/" + d1.getDate();
        this.finalDate = d2.getFullYear() + "/" + (d2.getMonth() + 1) + "/" + d2.getDate();

        break;

      case 2:

        this.initialDate = this.timeLine.month.date.getFullYear() + "/" + (this.timeLine.month.date.getMonth() + 1) + "/" + this.timeLine.month.date.getDate();
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "/" + (this.timeLine.month.lastDate.getMonth() + 1) + "/" + this.timeLine.month.lastDate.getDate();

        break;

      case 3:

        this.initialDate = null;
        this.finalDate = null;

        break;
    }
  }

  ngOnDestroy(): void {
    if (this.requestsSubscribe)
      this.requestsSubscribe.unsubscribe()
  }

  /**
   * Returns a translated accessibility label for the activity working state.
   * @param state The working state (0: Open, 1: In Progress, 2: Completed)
   * @returns Translated state label for screen readers
   */
  getStateLabel(state: number): string {
    const stateKeys: { [key: number]: string } = {
      0: 'widgets.user-activities.state-open',
      1: 'widgets.user-activities.state-in-progress',
      2: 'widgets.user-activities.state-completed'
    };
    return this.translateService.instant(stateKeys[state] || stateKeys[0]);
  }
}
